import { cva } from 'class-variance-authority';

/**
 * Content block class recipes.
 *
 * Like `Button.recipe`, `Accordion.recipe`, `ContentPanel.recipe` and the rest of this layer, these
 * contain ZERO `dark:` variants — colour comes entirely from the `--ds-content-block-*` slots that
 * `.ds-content-block` sets in `components/styles/design-system.css`, and that class needs no dark
 * rule at all because every slot it sets names a semantic role and a role already carries a value
 * per theme.
 *
 * ── Ported from Figma 1505:28837 on 2026-09-22, reworked the same day ─────────────────────────
 *
 * Three variants, two axes, nine component properties. Every number below is read off the set
 * rather than chosen; where a value here disagrees with anything written elsewhere in the
 * repository, the drawing is the reason and the disagreement is named.
 *
 * The rework collapsed the `Surface` axis from three values to two: `On surface` — a TRANSLUCENT
 * pair — was deleted, `On page` was renamed `Filled`, and `Filled` became the default. It is a
 * colour-and-naming change and NOT ONE NUMBER IN THIS FILE MOVED; the geometry table below lost two
 * rows and kept every value. Re-measured off the variant roots on 2026-09-22 to confirm that.
 *
 * The axes and where each is answered:
 *
 *   Surface   colour only — `data-surface` on the root, resolved to slots in `design-system.css`,
 *             plus the paint being dropped entirely for `none`.
 *   Layout    geometry only — a `layout` variant below. Inset, radius and vertical rhythm.
 *
 * And the axis product is THREE, not four: `Surface=None` ships `Layout=Inline` only. That is
 * enforced in the props type and again at runtime — see `types.ts` and `ContentBlock`.
 *
 * ── Measured geometry, per variant ────────────────────────────────────────────────────────────
 *
 * Read off the three variant roots and confirmed against a scratch instance driven through all
 * eight boolean combinations (description × body × footer), because a hidden layer's own geometry
 * is stale and cannot be trusted: the `↪ Content` slot reports a width of 430px inside a 420px
 * root while hidden, and 370px once shown.
 *
 *                              inset      radius   rhythm   total height, title only
 *   Filled  / Stacked          24         24       12       76   (1+24+26+24+1)
 *   Filled  / Inline           16         16       12       60   (1+16+26+16+1)
 *   None    / Inline           8 / 0      0        8        42   (8+26+8)
 *
 * Those totals are the arithmetic check that the rim is INSIDE the box. Figma's stroke align is
 * `INSIDE` on both boxed variants, so a 1px rim eats into the 24px inset rather than sitting
 * outside it — which is exactly what CSS `box-sizing: border-box` does, and this layer sets that
 * globally. The measured 76px would be 78 if either system drew the rim outside.
 *
 * Every step is a token rather than an arbitrary value, which is why there is not a single
 * `[24px]` below. `--spacing` is 4px and `--radius-md` / `--radius-lg` are the library's own 16
 * and 24, so `p-6`, `p-4`, `gap-3`, `gap-2`, `gap-1`, `py-2`, `rounded-lg` and `rounded-md`
 * compile to the drawn numbers and follow the library if it re-tunes them. Compare
 * `Accordion.recipe.ts`, which pins its radii as literals — the tokens had not been wired into the
 * Tailwind `--radius-*` namespace when it was written, and they have been since.
 *
 * ── Two frames in the drawing that are NOT reproduced, and why ────────────────────────────────
 *
 * The set's tree is root > `Header` > `Row_Title` > `Titles` > (`Title`, `Description`). Three
 * nested vertical frames to hold two text layers. Only one of them survives here.
 *
 * `Header` and `Row_Title` each have exactly ONE child, so every property they carry is inert:
 * `Header`'s item spacing is 36px in the stacked cells and 0 in the inline ones, and its
 * cross-axis alignment is MIN in stacked and CENTER in inline. None of that renders — a single
 * FILL-width child in an auto-layout frame is unaffected by gap or alignment. They are the
 * residue of the `Left` and `Right` header properties the rework deleted, and the 36px is a value
 * left behind by a layer that used to sit beside the titles.
 *
 * That is worth stating explicitly because 36px looks like a specification. It is not one: it
 * cannot appear on screen in any cell measured. Reproducing the two frames would mean writing two
 * wrappers whose only job is to carry values that do nothing, and reproducing the 36px would
 * change the rendering. (`Accordion`'s stacked trigger DID ship that 36px, where its `Header`
 * frame has two children and the gap does render; the set measures 16px there and the code was
 * corrected on 2026-09-22.)
 *
 * `Titles` is real — it carries the 4px between the heading and the description, in every cell —
 * and is the `header` part below.
 *
 * ── Long text, narrow columns and RTL ─────────────────────────────────────────────────────────
 *
 * Every part carries `min-w-0`, which is not defensive: a flex or grid item's default minimum size
 * is its content's intrinsic size, so a single unbroken token — a raw URL, a UUID, an untranslated
 * German compound — pushes its own column wider than the block and the copy runs out past the rim.
 * `min-w-0` lets the item shrink; `[overflow-wrap:anywhere]` is what then breaks the token.
 *
 * `anywhere` rather than `break-word`: they differ in exactly the case that matters here, which is
 * whether the unbreakable string is allowed to contribute to the container's intrinsic MINIMUM
 * width. `break-word` lets it, so the block still grows; `anywhere` does not. Nothing is clipped
 * either way — there is no `overflow-hidden` in this component, because a block that hid its own
 * overflow would hide a caller's popover too.
 *
 * Nothing is padded or aligned with a physical direction. The insets are symmetric, the stack is
 * vertical and every part is full-width, so the component needs no logical-property spelling to
 * work in RTL — there is no `pl-*`, `text-left` or `left-*` anywhere below to convert.
 *
 * ── No motion, deliberately ───────────────────────────────────────────────────────────────────
 *
 * Not one timing utility, which is why this file appears in `motionLiterals.test.ts`'s list of
 * recipes that carry none. A content block has no interactive state to transition between and no
 * open/close lifecycle to enter or leave: it is inert by being a drawing, the same reason
 * `ContentPanel`, `Badge` and `Divider` name no role. A recipe cannot tell a first mount from a
 * remount, so an entrance here would replay on every back-navigation.
 *
 * ── No `w-full` on the root ───────────────────────────────────────────────────────────────────
 *
 * The set draws every variant at 420px, which is a CANVAS width rather than a specification —
 * `counterAxisSizingMode` is `FIXED` because a component set has to be laid out somewhere, and the
 * instance probe confirms the root hugs its content vertically and takes whatever width it is
 * given horizontally.
 *
 * So the root is `flex` and `w-full` here, matching `accordionRootRecipe`: a titled section that
 * shrink-wrapped its heading would be a surprise in every column it was placed in, and a caller
 * who wants it narrower constrains it from outside. What the component does NOT do is pin 420px.
 */

/**
 * The `<section>`.
 *
 * `data-surface` and `data-layout` are on the root as attributes rather than being folded into the
 * class string. The stylesheet no longer needs `data-surface` at all — with one boxed surface and a
 * `none` that declares nothing, `.ds-content-block` carries no attribute-scoped rule — but both
 * attributes stay, because they are the stable handle a story's measurement pass and a unit test
 * assert on instead of matching a generated class name, and because `Accordion` still reads its own
 * `data-surface` in a selector, so the two components keep the same shape.
 *
 * `font-sans` for the reason the alert, the tag and the panel all set it: this layer omits
 * Tailwind's preflight, so without it a block's copy is typeset by whatever happens to be above it
 * in the tree, and the same block reads differently on a DS 2.0 page and inside a legacy MUI
 * screen.
 *
 * The rim, the radius and the fill are on the two boxed surfaces only. `none` drops all three
 * explicitly — `border-0 bg-transparent rounded-none` — rather than relying on a default, so a
 * caller's `className` cannot leave a half-painted box behind and the intent is legible in the
 * diff.
 */
export const contentBlockRootRecipe = cva(
  [
    'ds-content-block',
    'font-sans',
    'flex w-full min-w-0 flex-col',
    /*
     * The foreground is on the ROOT, not on the heading, and that is a correctness fix.
     *
     * The set's `↪ Content` slot carries no paint, which in Figma means "whatever the caller puts
     * in it". In CSS an unpainted body INHERITS, and what it inherits from is whatever sits above
     * the block in the tree — so an earlier revision that bound the colour only to the heading left
     * a caller's `<p>Body</p>` reading the surrounding page's colour. Inside a dark block that
     * measured 1.08:1 (#101828 on #0f0f0f). The axe pass in the stories caught it; no unit test,
     * type check or slot assertion would have.
     *
     * `.ds-panel` does the same thing for the same reason. The heading inherits from here and the
     * description overrides one step back.
     */
    'text-[var(--ds-content-block-fg)]',
  ],
  {
    variants: {
      /**
       * Inset, radius and the rhythm between the three regions.
       *
       * The rhythm is part of this axis rather than shared, because the set steps it with the inset:
       * 12px inside the boxed surface, 8px on none. It is not a separate decision a caller makes —
       * a 24px box with 8px between its regions is not a cell the design draws.
       *
       * `none` is absent from this variant map on purpose: `surface="none"` is its own layout cell
       * and is handled by `compoundVariants` below, so the two axes stay flat rather than becoming a
       * 3×2 matrix with one hole in it.
       */
      layout: {
        inline: 'gap-3 rounded-md p-4',
        stacked: 'gap-3 rounded-lg p-6',
      },
      surface: {
        /**
         * The boxed surface: an opaque fill with the decorative rim paired to it.
         *
         * Read the nesting note beside `.ds-content-block` before placing one inside a dialog or a
         * `.ds-panel-primary` — `--ds-content-block-bg` is the same token both of those use, so the
         * fill measures 1.00:1 there and only the rim separates the two. Documented, deliberate, and
         * not prevented.
         */
        filled: [
          'border-solid border-[length:var(--ds-content-block-border-width)] border-[var(--ds-content-block-border)]',
          'bg-[var(--ds-content-block-bg)]',
        ],
        /**
         * Bare type. No fill, no rim, no radius, and NO horizontal inset at all — the set's root is
         * `8/0/8/0`, so the block's copy aligns flush with whatever is above and below it in the
         * container that already has the inset. That flush alignment is the whole point of the
         * variant; giving it even 4px horizontally would make it read as a box with an invisible
         * edge.
         *
         * It is also the only value that is safe on a host this component cannot see, now that the
         * boxed surface is opaque.
         */
        none: 'gap-2 rounded-none border-0 bg-transparent px-0 py-2',
      },
    },
    compoundVariants: [
      /*
       * `surface="none"` wins its geometry back from whichever layout the caller passed.
       *
       * The type only admits `inline` there, but `cva` receives the resolved values at runtime and
       * `layout: 'inline'`'s `p-4 rounded-md` would otherwise merge with the surface's `px-0 py-2
       * rounded-none` and be decided by `tailwind-merge`'s ordering rather than by intent. Stating it
       * makes the outcome independent of which class happens to come last.
       */
      { surface: 'none', layout: ['inline', 'stacked'], class: 'gap-2 rounded-none px-0 py-2' },
    ],
    defaultVariants: { layout: 'stacked', surface: 'filled' },
  }
);

/**
 * The header: the heading and its optional description, 4px apart.
 *
 * `gap-1` is the `Titles` frame's own item spacing, bound to `x1` in the set and identical in every
 * cell. It is a separate element from the root rather than two children of it, because the
 * root's rhythm is 12px and the pair inside the header is 4px — they are two different rhythms and
 * one flex container can only have one.
 *
 * Present whether or not there is a description, which is deliberate: the heading is required, so
 * the header is never empty and never needs to collapse. Compare the body and footer below.
 */
export const contentBlockHeaderRecipe = cva(['flex w-full min-w-0 flex-col gap-1']);

/**
 * The heading.
 *
 * `text-heading-xs` is the `Heading/xs` text style bound to the `Title` layer in every cell:
 * 16px / 26px / 500. Read off the style NAME rather than transcribed as three arbitrary values, so
 * a library retune reaches this component without an edit here.
 *
 * No colour of its own: it INHERITS `--ds-content-block-fg` from the root. That is deliberate and
 * the reason is in the root recipe above — the body has to inherit a colour from somewhere inside
 * the block, and the only place that can be is the root, so restating it here would be a second
 * declaration of the same fact and the one a future edit would forget to move.
 *
 * `m-0` because this is a real `<h1>`–`<h6>` and this layer omits Tailwind's preflight, so the
 * browser's own heading margin is still live and would break the 4px gap above and the 12px below.
 * The Natalie panels being migrated onto this component carry exactly that `m-0` by hand today.
 */
export const contentBlockTitleRecipe = cva(['m-0 min-w-0 text-heading-xs [overflow-wrap:anywhere]']);

/**
 * The description.
 *
 * `text-body-md` is the `Body/md` style on the `Description` layer in every cell: 14px / 20px /
 * 400. Note this is `md` and not the `sm` the two Natalie panels currently use — the drawing is the
 * reason, and it is a visible change to those two screens when they migrate.
 *
 * `m-0` for the same preflight reason as the heading.
 */
export const contentBlockDescriptionRecipe = cva([
  'm-0 min-w-0 text-body-md text-[var(--ds-content-block-description)] [overflow-wrap:anywhere]',
]);

/**
 * The body.
 *
 * A full-width block that imposes nothing on what is inside it, which is the same call
 * `contentPanelRecipe` documents at length: a flex column here would stack the caller's DIRECT
 * children only, so wrapping two of them in a fragment or a div would produce a different result
 * for a reason nothing on screen explains. A caller who wants a stack composes one, one line,
 * visible where they wrote it.
 *
 * It does carry `min-w-0` and `[overflow-wrap:anywhere]`, because those are containment rather than
 * layout: without them a single long token inside a caller's content widens the whole block.
 */
export const contentBlockContentRecipe = cva(['w-full min-w-0 [overflow-wrap:anywhere]']);

/**
 * The footer: supporting text on the left, actions on the right.
 *
 * ── This OWNS its row, and that reversed an earlier decision ─────────────────────────────────
 *
 * The first revision of this port shipped the footer layout-NEUTRAL — a plain full-width block,
 * with the caller composing any row inside it. That followed the reviewed plan, which had decided
 * the component should own no alignment. It was wrong, and the evidence that settled it is worth
 * writing down because it is the kind a single reading of the component set cannot give you.
 *
 * Re-audited 2026-09-22 across the whole `Content Block [In progress]` page: **22 instances of
 * this set exist, and all 22 draw the same footer row** — `HORIZONTAL`, `SPACE_BETWEEN`,
 * cross-axis `CENTER`, 12px gap, with a `Body/md` label on the left and a `Footer_Actions` frame
 * on the right holding up to two buttons 4px apart. That includes the four numbered patterns in
 * `Usage — Light` and `Usage — Dark`, the `Proposal: before / after` comparison, and the two full
 * screen mockups. Not one instance departs from it.
 *
 * The set description says the same thing, and now the drawing backs it: "Its default matches
 * Natalie: supporting text on the left and up to two actions on the right."
 *
 * ── Why the count-dependence objection went away ─────────────────────────────────────────────
 *
 * The original argument against owning `space-between` was that it is the one flex value whose
 * meaning changes with the number of children — one child sits at the start, two are pushed apart,
 * three spread. That is still true, and it is no longer a problem here, because the drawing always
 * has exactly TWO children: the label and the actions FRAME. `Footer_Actions` exists even when a
 * button inside it is hidden — the `Test your Natalie` block on the `C · Review your Natalie`
 * mockup hides its `Copy link` and keeps the frame — so the row never silently re-aligns.
 *
 * A caller who genuinely needs something else passes ONE child with `w-full` on it and gets the
 * whole width to themselves, which is the set's own instruction ("Replace the whole slot when
 * another footer layout is needed") expressed in CSS.
 *
 * ── `flex-wrap` is the one thing added to the drawing ────────────────────────────────────────
 *
 * Figma cannot express wrapping. Without it a label and two buttons overflow a narrow column, and
 * the `Narrow` story measures exactly that at 240px in both directions. Wrapping is additive: at
 * the drawn width the row is identical to the set.
 *
 * ── What does NOT go in here ─────────────────────────────────────────────────────────────────
 *
 * A primary/filled button. Across all 22 instances, and in both screen mockups, a Content Block
 * footer holds only `Type=Muted` ("Copy link") plus `Type=Secondary` ("Open"), both at
 * `Size=Small` — 32px, which is this repo's `size="xs"`. The page's primary action is drawn as a
 * `Size=Medium` `Type=Primary` button in its OWN row outside the blocks (`Publish row` on the
 * review screen, `Submit row` on the create screen) and duplicated in the screen header.
 *
 * So a `Republish`-style primary action does not belong in this slot. The stories show the drawn
 * pair and the page-level primary side by side so a call site copies the right one.
 */
export const contentBlockFooterRecipe = cva([
  'flex w-full min-w-0 flex-row flex-wrap items-center justify-between gap-3',
  '[overflow-wrap:anywhere]',
]);
